import { Injectable, WritableSignal, signal } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { Capacitor } from '@capacitor/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { FingerprintService } from '@pages/auth/pin/services/fingerprint.service';

import { BaseProfileCommand } from '@shared/commands/base-profile.command';
import { TFingerprintType } from '@shared/models/fingerprint.model';
import { IProfileCommandContext } from '@shared/models/profile-command.model';
import { AuthService } from '@shared/services/auth.service';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

@Injectable()
export class EnableFingerprintCommand extends BaseProfileCommand {
  private _dynamicTitle = 'Вход по отпечатку или FaceID';

  public readonly checkbox = 'unchecked' as const;
  public readonly group = 'display';
  public readonly hidden = false;
  public readonly icon = 'auth-finger';
  public readonly order = 4;
  public readonly settingsNames?: string[] = undefined;
  public readonly subtitle?: WritableSignal<string> = undefined;
  public readonly type = 'enableFingerprint';
  public readonly withAppendIcon = false;
  readonly checkboxChecked = signal(false);
  readonly checkboxDisabled = signal(false);

  public get title(): string {
    return this._dynamicTitle;
  }

  constructor(
    private fingerprintService: FingerprintService,
    private authService: AuthService,
    protected commandAvailabilityService?: CommandAvailabilityService,
    protected appSettingsService?: AppSettingsService
  ) {
    super(commandAvailabilityService, appSettingsService);
    this.initializeCheckboxState();
  }

  public async execute(_context: IProfileCommandContext): Promise<void> {
    console.log('EnableFingerprintCommand.execute - before:', this.checkboxChecked);
    console.log('EnableFingerprintCommand.execute - type:', this.type);

    // Используем базовое переключение чекбокса с автоматическим сохранением
    const newState = await this.executeToggle();

    console.log('EnableFingerprintCommand.execute - after:', this.checkboxChecked, 'newState:', newState);

    try {
      if (newState) {
        // Добавляем логику для iOS и регистрацию биометрии
        const stream$ = Capacitor.getPlatform() === 'ios' ? this.fingerprintService.showDialog() : of(true);

        await stream$
          .pipe(switchMap(() => this.fingerprintService.registerBiometricSecret(this.authService.userPassword)))
          .toPromise();

        this.fingerprintService.setEnableFingerPrint(true);
      } else {
        this.fingerprintService.setEnableFingerPrint(false);
      }

      console.log(`Fingerprint ${newState ? 'enabled' : 'disabled'}`);

      // Проверим что реально сохранилось
      const savedValue = await this.getSetting<boolean>();

      console.log('EnableFingerprintCommand.execute - saved value:', savedValue);
    } catch (error) {
      this.fingerprintService.setEnableFingerPrint(false);
      void this.executeToggle();
      console.error('Error executing fingerprint command:', error);
    }
  }

  /**
   * Переключает состояние чекбокса (вызывается при клике на чекбокс)
   */
  public async toggle(): Promise<void> {
    console.log('EnableFingerprintCommand.toggle called');
    await this.execute({ data: {} });
  }

  /**
   * Инициализирует состояние чекбокса из настроек
   */
  private async initializeCheckboxState() {
    // Состояние уже установлено в конструкторе из кэша, проверяем только доступность биометрии
    this.appSettingsService
      .getSetting(this.appSettingsService.settingsKeyWithName.enableFingerprint)
      .pipe(take(1))
      .subscribe((enabled) => {
        this.updateCheckboxState(TypeUtilService.isTruthy(enabled));
      });
  }

  /**
   * Устанавливает заголовок в зависимости от типа биометрии
   */
  private setTitleForFingerprintType(type: TFingerprintType): void {
    switch (type) {
      case 'finger':
        this._dynamicTitle = 'Вход по отпечатку пальца';
        break;

      case 'face':
        this._dynamicTitle = 'Вход по FaceID';
        break;

      default:
        this._dynamicTitle = 'Вход по отпечатку или FaceID';
        break;
    }
  }

  protected checkAdditionalAvailability(_context: IProfileCommandContext): Observable<boolean> | boolean | undefined {
    return this.fingerprintService.isAvailable().pipe(
      map((fingerRes) => {
        this.setTitleForFingerprintType(fingerRes.type);
        return fingerRes.available;
      })
    );
  }
}
